
import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const ROICalculatorPage = () => {
  const [inputs, setInputs] = useState({
    currentRate: '',
    currentBookings: '',
    photographyCost: '',
    email: '',
    name: ''
  });
  
  const [results, setResults] = useState({
    newRate: 0,
    newBookings: 0,
    additionalRevenue: 0,
    roi: 0,
    paybackMonths: 0
  });
  
  const [showResults, setShowResults] = useState(false);
  const [emailCaptured, setEmailCaptured] = useState(false);

  const calculateROI = () => {
    const currentRate = parseFloat(inputs.currentRate) || 0;
    const currentBookings = parseFloat(inputs.currentBookings) || 0;
    const photographyCost = parseFloat(inputs.photographyCost) || 300;
    
    // Professional photos typically increase rates by 20% and bookings by 30%
    const newRate = currentRate * 1.2;
    const newBookings = currentBookings * 1.3;
    
    const currentRevenue = currentRate * currentBookings * 12;
    const newRevenue = newRate * newBookings * 12;
    const additionalRevenue = newRevenue - currentRevenue;
    
    const roi = ((additionalRevenue - photographyCost) / photographyCost) * 100;
    const paybackMonths = photographyCost / (additionalRevenue / 12);
    
    setResults({
      newRate: Math.round(newRate),
      newBookings: Math.round(newBookings),
      additionalRevenue: Math.round(additionalRevenue),
      roi: Math.round(roi),
      paybackMonths: Math.round(paybackMonths * 10) / 10
    });
    
    setShowResults(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setInputs(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const captureEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const formData = new FormData();
    formData.append('name', inputs.name);
    formData.append('email', inputs.email);
    formData.append('current_rate', inputs.currentRate);
    formData.append('current_bookings', inputs.currentBookings);
    formData.append('estimated_roi', results.roi.toString());
    formData.append('lead_source', 'roi_calculator');

    try {
      const response = await fetch('https://readdy.ai/api/form/d3vhs1eg28apfrolkiq0', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        setEmailCaptured(true);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  useEffect(() => {
    if (inputs.currentRate && inputs.currentBookings) {
      calculateROI();
    }
  }, [inputs.currentRate, inputs.currentBookings, inputs.photographyCost]);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <div className="pt-20 pb-16 bg-gradient-to-br from-green-50 to-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Airbnb Photography 
              <span className="text-green-600"> ROI Calculator</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover how professional photography can boost your Airbnb revenue. Calculate your potential return on investment in seconds.
            </p>
          </div>
        </div>
      </div>

      {/* Calculator Section */}
      <div className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Calculate Your ROI</h2>
              <div className="bg-gray-50 rounded-lg p-8">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Current Nightly Rate ($)
                    </label>
                    <input
                      type="number"
                      name="currentRate"
                      value={inputs.currentRate}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                      placeholder="e.g., 120"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Monthly Bookings (Average)
                    </label>
                    <input
                      type="number"
                      name="currentBookings"
                      value={inputs.currentBookings}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                      placeholder="e.g., 15"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Photography Investment ($)
                    </label>
                    <input
                      type="number"
                      name="photographyCost"
                      value={inputs.photographyCost}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                      placeholder="300"
                    />
                    <p className="text-xs text-gray-500 mt-1">Average cost for professional Airbnb photography</p>
                  </div>
                </div>
                
                <div className="mt-8 p-6 bg-blue-50 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Based on Industry Data:</h3>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Professional photos increase nightly rates by 20%</li>
                    <li>• Booking frequency increases by 30%</li>
                    <li>• Average payback period: 2-3 months</li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              {showResults && (
                <div className="sticky top-24">
                  <h2 className="text-3xl font-bold text-gray-900 mb-8">Your Potential Results</h2>
                  <div className="bg-gradient-to-br from-green-50 to-white rounded-lg p-8 border-2 border-green-100">
                    <div className="grid grid-cols-2 gap-6 mb-8">
                      <div className="text-center">
                        <div className="text-3xl font-bold text-green-600 mb-2">
                          ${results.newRate}
                        </div>
                        <div className="text-sm text-gray-600">New Nightly Rate</div>
                        <div className="text-xs text-green-600">
                          +${results.newRate - parseFloat(inputs.currentRate || '0')} increase
                        </div>
                      </div>
                      
                      <div className="text-center">
                        <div className="text-3xl font-bold text-green-600 mb-2">
                          {results.newBookings}
                        </div>
                        <div className="text-sm text-gray-600">Monthly Bookings</div>
                        <div className="text-xs text-green-600">
                          +{results.newBookings - parseFloat(inputs.currentBookings || '0')} more bookings
                        </div>
                      </div>
                    </div>
                    
                    <div className="border-t border-green-200 pt-6">
                      <div className="text-center mb-6">
                        <div className="text-4xl font-bold text-green-600 mb-2">
                          ${results.additionalRevenue.toLocaleString()}
                        </div>
                        <div className="text-lg text-gray-700 font-semibold">Additional Annual Revenue</div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-center">
                        <div className="bg-white rounded-lg p-4">
                          <div className="text-2xl font-bold text-gray-900">{results.roi}%</div>
                          <div className="text-sm text-gray-600">Return on Investment</div>
                        </div>
                        <div className="bg-white rounded-lg p-4">
                          <div className="text-2xl font-bold text-gray-900">{results.paybackMonths}</div>
                          <div className="text-sm text-gray-600">Months to Break Even</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {!emailCaptured && results.roi > 0 && (
                    <div className="mt-8 bg-gray-50 rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Get Your Detailed ROI Report</h3>
                      <form onSubmit={captureEmail} data-readdy-form className="space-y-4">
                        <div>
                          <input
                            type="text"
                            name="name"
                            required
                            value={inputs.name}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                            placeholder="Your Name"
                          />
                        </div>
                        <div>
                          <input
                            type="email"
                            name="email"
                            required
                            value={inputs.email}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                            placeholder="Email Address"
                          />
                        </div>
                        <button
                          type="submit"
                          className="w-full bg-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-green-700 transition-colors whitespace-nowrap"
                        >
                          Send My ROI Report
                        </button>
                      </form>
                    </div>
                  )}
                  
                  {emailCaptured && (
                    <div className="mt-8 bg-green-50 rounded-lg p-6 text-center">
                      <i className="ri-check-line text-2xl text-green-600 mb-2"></i>
                      <h3 className="font-semibold text-gray-900 mb-2">Report Sent!</h3>
                      <p className="text-gray-600">Check your email for your detailed ROI analysis and next steps.</p>
                    </div>
                  )}
                </div>
              )}
              
              {!showResults && (
                <div className="flex items-center justify-center h-96 bg-gray-50 rounded-lg">
                  <div className="text-center">
                    <i className="ri-calculator-line text-6xl text-gray-400 mb-4"></i>
                    <p className="text-gray-500">Enter your details to see your ROI calculation</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-16 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Ready to Boost Your Airbnb Revenue?</h2>
          <p className="text-xl text-gray-300 mb-8">
            Professional photography is one of the fastest ways to increase your booking rate and nightly prices.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/pricing-guide"
              className="bg-white text-gray-900 py-3 px-8 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              Get Pricing Guide
            </a>
            <a
              href="/#contact"
              className="bg-green-600 text-white py-3 px-8 rounded-lg font-semibold hover:bg-green-700 transition-colors whitespace-nowrap"
            >
              Book Photography Session
            </a>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ROICalculatorPage;
